import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { appStore } from '../stores/AppStore';

interface UserPresenceData {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  status?: string;
  lastActivity: number;
  browserInfo?: string;
  osInfo?: string;
  deviceType?: string;
  connectionQuality?: string;
  cursorPosition?: { x: number; y: number };
  color?: string;
}

interface UsePresenceOptions {
  roomType: 'project' | 'mindmap';
  roomId: string;
  enableCursor?: boolean;
}

export function usePresence({ roomType, roomId, enableCursor = false }: UsePresenceOptions) {
  const [myPresence, setMyPresence] = useState<UserPresenceData | null>(null);
  const [peers, setPeers] = useState<Record<string, UserPresenceData>>({});
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  
  const authToken = appStore.auth.accessToken;
  const user = appStore.auth.user;
  const userProfile = appStore.user.currentOrganization;

  // Initialize WebSocket connection
  useEffect(() => {
    // TEMPORARILY DISABLED: Presence server not yet implemented
    // This prevents repeated connection attempts that spam the console
    return;
    
    if (!authToken || !user) return;

    // Create socket connection to presence server
    const socket = io('http://localhost:4011/presence', {
      auth: {
        token: authToken,
      },
      transports: ['websocket'],
      path: '/socket.io/',
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[Presence] Connected to presence server');
      setIsConnected(true);
      
      // Join the appropriate room
      const presenceData: UserPresenceData = {
        id: user.id,
        name: userProfile ? `${userProfile.firstName} ${userProfile.lastName}`.trim() : user.email,
        email: user.email,
        avatar: userProfile?.avatar,
        status: 'online',
        lastActivity: Date.now(),
        browserInfo: navigator.userAgent,
        deviceType: /Mobile/.test(navigator.userAgent) ? 'mobile' : 'desktop',
      };
      
      setMyPresence(presenceData);
      
      socket.emit('join-presence', {
        roomType,
        roomId,
        presence: presenceData,
      });
    });

    socket.on('disconnect', () => {
      console.log('[Presence] Disconnected from presence server');
      setIsConnected(false);
    });

    socket.on('presence-update', (data: { userId: string; presence: UserPresenceData }) => {
      if (data.userId !== user.id) {
        setPeers(prev => ({
          ...prev,
          [data.userId]: data.presence,
        }));
      }
    });

    socket.on('user-left', (data: { userId: string }) => {
      setPeers(prev => {
        const updated = { ...prev };
        delete updated[data.userId];
        return updated;
      });
    });

    socket.on('room-presence', (data: { users: Record<string, UserPresenceData> }) => {
      // Remove our own presence from peers
      const peersOnly = { ...data.users };
      delete peersOnly[user.id];
      setPeers(peersOnly);
    });

    return () => {
      socket.emit('leave-presence', { roomType, roomId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [authToken, user, userProfile, roomType, roomId]);

  // Publish presence updates
  const publishPresence = useCallback((updates: Partial<UserPresenceData>) => {
    if (!socketRef.current || !myPresence || !isConnected) return;
    
    const updatedPresence = {
      ...myPresence,
      ...updates,
      lastActivity: Date.now(),
    };
    
    setMyPresence(updatedPresence);
    
    socketRef.current.emit('update-presence', {
      roomType,
      roomId,
      presence: updatedPresence,
    });
  }, [myPresence, roomType, roomId, isConnected]);

  // Publish cursor position (for collaborative features)
  const publishCursor = useCallback((position: { x: number; y: number } | null) => {
    if (!enableCursor || !socketRef.current || !isConnected) return;
    
    socketRef.current.emit('cursor-move', {
      roomType,
      roomId,
      position,
    });
  }, [enableCursor, roomType, roomId, isConnected]);

  return {
    user: myPresence,
    peers,
    publishPresence,
    publishCursor,
    isConnected,
  };
}
